/* eslint-disable react/require-default-props */
import { FC } from 'react';
import Grid from '@mui/material/Grid';
import strings from '@strings/LocalisedStrings';
import { dateFormatter, percentageFormatter, numberFormatterHoldings } from '@utils/GenericUtils';
import { Typography, useTheme } from '@mui/material';
import Colors from '@src/app/config/colorConfig';

interface HoldingSecurityDetailsProps {
  holdingsDate: string;
  securityCode: string;
  securityName: string;
  units: number;
  price: number;
  value: number;
  costBase: number;
  estimatedGainOrLoss: number;
  percentageOfPortfolio: number;
  totalReturnValue: number;
  priceDate: Date | string;
}

interface HoldingSecurityInfoColumnProps {
  title: string;
  desc?: string | number;
}

const HoldingSecurityInfoColumn: FC<HoldingSecurityInfoColumnProps> = ({ title, desc }): JSX.Element => {
  const theme = useTheme();
  return (
    <Grid item container direction="column" xs={12} sm={6} md={3} rowSpacing={1}>
      <Grid item>
        <Typography color={theme.palette.primary.main} fontWeight={700} fontSize={14}>
          {title}
        </Typography>
      </Grid>
      {desc && (
        <Grid item>
          <Typography fontWeight={700} fontSize={20}>
            {desc}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

const HoldingSecurityDetails: FC<HoldingSecurityDetailsProps> = ({
  holdingsDate,
  securityCode,
  securityName,
  units,
  price,
  priceDate,
  value,
  percentageOfPortfolio,
  costBase,
  estimatedGainOrLoss,
  totalReturnValue,
}): JSX.Element => {
  const theme = useTheme();
  return (
    <Grid item container direction="column" mb={4}>
      <Grid item>
        <Typography fontWeight={700} fontSize={36}>
          {securityName}
        </Typography>
      </Grid>
      <Grid item>
        <Typography fontWeight={700} fontSize={20} color={Colors.gray800}>
          {securityCode}
        </Typography>
      </Grid>
      <Grid item mt={3} mb={3}>
        <Typography color={theme.palette.primary.main} textTransform="uppercase" fontWeight={700} fontSize={14}>
          {strings.detailsAsOf} {dateFormatter(holdingsDate)}
        </Typography>
      </Grid>
      <Grid item container spacing={4}>
        <HoldingSecurityInfoColumn title={strings.units} desc={numberFormatterHoldings(units, 4)} />
        <HoldingSecurityInfoColumn title={strings.price} desc={`$${numberFormatterHoldings(price, 4)}`} />
        <HoldingSecurityInfoColumn title={strings.priceDate} desc={dateFormatter(priceDate, false)} />
        <HoldingSecurityInfoColumn title={strings.value} desc={`$${numberFormatterHoldings(value, 2)}`} />
        <HoldingSecurityInfoColumn title={strings.weight} desc={percentageFormatter(percentageOfPortfolio || 0)} />
        <HoldingSecurityInfoColumn title={strings.cost} desc={`$${numberFormatterHoldings(costBase, 2)}`} />
        <HoldingSecurityInfoColumn title={strings.estLossGain} desc={`$${numberFormatterHoldings(estimatedGainOrLoss, 2)}`} />
        <HoldingSecurityInfoColumn title={strings.totalReturnInception} desc={`$${numberFormatterHoldings(totalReturnValue, 2)}`} />
      </Grid>
    </Grid>
  );
};

export default HoldingSecurityDetails;
